import React, { useState, useEffect, useRef, useCallback } from 'react';

const D = {
  bg:'#060912', bg2:'#0a1020', panel:'#0e1626', panel2:'#13203a',
  line:'#22324f', ink:'#eaf1ff', muted:'#8ea3c9', accent:'#4cc9ff', accent2:'#ff3fb4',
  A:'#3ff2a6', T:'#ff5d7a', G:'#ffcf3f', C:'#4cc9ff', U:'#c08bff',
};
const BC = { A:D.A, T:D.T, G:D.G, C:D.C, U:D.U };

const CODON_STR = 'FFLLSSSSYY**CC*WLLLLPPPPHHQQRRRRIIIMTTTTNNKKSSRRVVVVAAAADDEEGGGG';
function codonAA(cod) {
  const o = 'UCAG';
  return CODON_STR[o.indexOf(cod[0]) * 16 + o.indexOf(cod[1]) * 4 + o.indexOf(cod[2])];
}
const AA_TABLE = {
  F:['Phe','Phenylalanine','np'], L:['Leu','Leucine','np'], I:['Ile','Isoleucine','np'],
  M:['Met','Methionine','np'], V:['Val','Valine','np'], P:['Pro','Proline','np'],
  A:['Ala','Alanine','np'], W:['Trp','Tryptophan','np'], G:['Gly','Glycine','np'],
  S:['Ser','Serine','po'], T:['Thr','Threonine','po'], Y:['Tyr','Tyrosine','po'],
  C:['Cys','Cysteine','po'], N:['Asn','Asparagine','po'], Q:['Gln','Glutamine','po'],
  H:['His','Histidine','ba'], K:['Lys','Lysine','ba'], R:['Arg','Arginine','ba'],
  D:['Asp','Aspartate','ac'], E:['Glu','Glutamate','ac'], '*':['Stop','Stop codon','st'],
};
const AA_COL = { np:'#5ad1ff', po:'#3ff2a6', ba:'#ffcf3f', ac:'#ff6f91', st:'#ff3b3b' };

const HELIX_SEQ = 'ATGCATCGGCTAACGTAGC';
const COMP = { A:'T', T:'A', G:'C', C:'G' };
const HN = HELIX_SEQ.length, H_GAP = 24, H_TWIST = 32, H_R = 66;

const ZOOM_SVG = [
  '<svg viewBox="0 0 100 100"><circle cx="50" cy="24" r="13" fill="none" stroke="#4cc9ff" stroke-width="3"/><path d="M50 38 L50 74 M50 46 L30 64 M50 46 L70 64 M50 74 L38 95 M50 74 L62 95" fill="none" stroke="#4cc9ff" stroke-width="3" stroke-linecap="round"/></svg>',
  '<svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="44" fill="rgba(76,201,255,.08)" stroke="#4cc9ff" stroke-width="2"/><circle cx="50" cy="50" r="17" fill="rgba(255,63,180,.25)" stroke="#ff3fb4" stroke-width="2.5"/><circle cx="28" cy="34" r="4" fill="#3ff2a6"/><circle cx="72" cy="36" r="5" fill="#ffcf3f"/><circle cx="30" cy="68" r="5" fill="#c08bff"/><circle cx="70" cy="70" r="4" fill="#3ff2a6"/></svg>',
  '<svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="40" fill="rgba(255,63,180,.12)" stroke="#ff3fb4" stroke-width="2.5"/><path d="M28 40 q12 -10 24 0 t24 0 M24 56 q12 10 26 0 t24 2 M30 68 q10 -8 22 2" fill="none" stroke="#4cc9ff" stroke-width="2.4" stroke-linecap="round" opacity=".85"/></svg>',
  '<svg viewBox="0 0 100 100"><g stroke-linecap="round"><line x1="34" y1="20" x2="66" y2="80" stroke="#4cc9ff" stroke-width="13"/><line x1="66" y1="20" x2="34" y2="80" stroke="#4cc9ff" stroke-width="13"/><circle cx="50" cy="50" r="6" fill="#060912" stroke="#ff3fb4" stroke-width="3"/></g></svg>',
  '<svg viewBox="0 0 100 100"><g stroke-width="3" fill="none" stroke-linecap="round"><path d="M35 8 C70 25 70 42 35 58 C0 75 0 92 35 96" stroke="#4cc9ff"/><path d="M65 8 C30 25 30 42 65 58 C100 75 100 92 65 96" stroke="#3ff2a6"/><line x1="38" y1="18" x2="62" y2="18" stroke="#ffcf3f"/><line x1="33" y1="33" x2="67" y2="33" stroke="#ff5d7a"/><line x1="38" y1="48" x2="62" y2="48" stroke="#c08bff"/><line x1="33" y1="70" x2="67" y2="70" stroke="#ffcf3f"/><line x1="38" y1="85" x2="62" y2="85" stroke="#ff5d7a"/></g></svg>',
];
const ZOOM_LEVELS = [
  { label:'You', scale:'1.7 m', sub:'A whole human body', desc:'You are built from about 37 trillion cells. Almost every one carries a complete copy of your DNA.' },
  { label:'A single cell', scale:'~30 µm', sub:'One of 37 trillion', desc:'Zoom into one cell. Floating at its centre is a protected vault — the nucleus.' },
  { label:'The nucleus', scale:'~6 µm', sub:"The cell's control room", desc:'Inside the nucleus, your DNA is stored, read, and copied — kept safe from the rest of the cell.' },
  { label:'A chromosome', scale:'~2 µm', sub:'DNA, tightly wound', desc:'To fit 2 metres of DNA into a microscopic nucleus, it coils tightly around proteins into 46 chromosomes.' },
  { label:'The double helix', scale:'2 nm wide', sub:'The molecule of life', desc:'Unwind it completely and there it is: the twisting ladder of base pairs that spells out all of you.' },
];

const CHR_LEN = [100,98,80,77,73,69,64,59,57,55,54,53,46,43,41,36,34,33,24,26,18,20];
const CHR_FACTS = {
  1:'The largest chromosome — it carries the most genes (~2,000), including ones for the nervous system.',
  6:'Holds the immune-system HLA genes that help your body tell self from invader.',
  7:'Home to CFTR; faults here cause cystic fibrosis.',
  11:'Carries HBB, the gene for part of haemoglobin — sickle-cell anaemia traces to a single letter here.',
};

const AA_NAMES = { A:'Adenine', T:'Thymine', G:'Guanine', C:'Cytosine' };
const TABS = [['01','The Zoom'],['02','Double Helix'],['03','The Code'],['04','Replication'],['05','Genome & You']];
const FD = "'Space Grotesk', sans-serif";
const FB = "'DM Sans', sans-serif";
const FM = "'IBM Plex Mono', monospace";

export default function DnaExplorer() {
  const [tab, setTab] = useState(0);
  const [zoom, setZoom] = useState(0);
  const [spinning, setSpinning] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [selPair, setSelPair] = useState(0);
  const [dna, setDna] = useState('ATGTTCGGCAGCTAA');
  const [codeStep, setCodeStep] = useState(0);
  const [mutNote, setMutNote] = useState('');
  const [repT, setRepT] = useState(0);
  const [repPlaying, setRepPlaying] = useState(false);
  const [sex, setSex] = useState('XY');
  const [selChr, setSelChr] = useState(1);
  const [p1, setP1] = useState('Bb');
  const [p2, setP2] = useState('Bb');

  const helixRef = useRef(null);
  const helixRAFRef = useRef(null);
  const angleRef = useRef(0);
  const dragRef = useRef(false);
  const lastXRef = useRef(0);
  const spinRef = useRef(spinning);
  const speedRef = useRef(speed);
  spinRef.current = spinning;
  speedRef.current = speed;
  const repRAFRef = useRef(null);

  // Inject keyframes once
  useEffect(() => {
    const s = document.createElement('style');
    s.dataset.dnaKf = '1';
    s.textContent = '@keyframes dnaFloaty{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}';
    if (!document.querySelector('[data-dna-kf]')) document.head.appendChild(s);
    return () => { if (s.parentNode) s.parentNode.removeChild(s); };
  }, []);

  // Callback ref: starts/stops RAF when helix tab mounts/unmounts
  const attachHelix = useCallback((el) => {
    if (el) {
      helixRef.current = el;
      const loop = () => {
        if (!helixRef.current) { helixRAFRef.current = null; return; }
        if (spinRef.current && !dragRef.current) angleRef.current += speedRef.current * 0.6;
        helixRef.current.style.transform = `rotateY(${angleRef.current}deg)`;
        helixRAFRef.current = requestAnimationFrame(loop);
      };
      helixRAFRef.current = requestAnimationFrame(loop);
    } else {
      helixRef.current = null;
      if (helixRAFRef.current) { cancelAnimationFrame(helixRAFRef.current); helixRAFRef.current = null; }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (repRAFRef.current) { cancelAnimationFrame(repRAFRef.current); repRAFRef.current = null; }
    setRepPlaying(false);
  }, [tab]);

  useEffect(() => () => {
    if (helixRAFRef.current) cancelAnimationFrame(helixRAFRef.current);
    if (repRAFRef.current) cancelAnimationFrame(repRAFRef.current);
  }, []);

  // Helix pointer drag
  const onHelixDown = (e) => { dragRef.current = true; lastXRef.current = e.clientX; e.currentTarget.setPointerCapture(e.pointerId); };
  const onHelixMove = (e) => { if (dragRef.current) { angleRef.current += (e.clientX - lastXRef.current) * 0.6; lastXRef.current = e.clientX; } };
  const onHelixUp = () => { dragRef.current = false; };

  // Code tab
  const cycleBase = (i) => {
    const o = 'ATGC';
    setDna(prev => { const a = prev.split(''); a[i] = o[(o.indexOf(a[i]) + 1) % 4]; return a.join(''); });
    setCodeStep(0); setMutNote('');
  };
  const toProtein = (d) => {
    const m = d.replace(/T/g, 'U'); const out = [];
    for (let i = 0; i + 3 <= m.length; i += 3) {
      const aa = codonAA(m.slice(i, i + 3)); out.push(AA_TABLE[aa][0]); if (aa === '*') break;
    }
    return out.join('·');
  };
  const doMutate = () => {
    const before = toProtein(dna); const o = 'ATGC'; const arr = dna.split('');
    const i = Math.floor(Math.random() * arr.length);
    let nb = arr[i]; while (nb === arr[i]) nb = o[Math.floor(Math.random() * 4)];
    arr[i] = nb; const nd = arr.join(''); const after = toProtein(nd);
    const note = before === after ? 'Silent mutation — the protein is unchanged.'
      : after.includes('Stop') && !before.includes('Stop') && after.split('·').length < before.split('·').length
        ? 'Nonsense mutation — a stop codon cut the protein short!'
        : 'Missense mutation — one amino acid changed.';
    setDna(nd); setCodeStep(2); setMutNote('Position ' + (i + 1) + ': ' + note);
  };

  // Replication RAF
  const repPlay = () => {
    if (repPlaying) { if (repRAFRef.current) cancelAnimationFrame(repRAFRef.current); repRAFRef.current = null; setRepPlaying(false); return; }
    const from = repT >= 0.999 ? 0 : repT; const t0 = performance.now(); const dur = 4200;
    setRepPlaying(true); setRepT(from);
    const tick = (now) => { const k = Math.min(1, from + (now - t0) / dur); setRepT(k); if (k < 1) repRAFRef.current = requestAnimationFrame(tick); else { repRAFRef.current = null; setRepPlaying(false); } };
    repRAFRef.current = requestAnimationFrame(tick);
  };
  const repScrub = (e) => { if (repRAFRef.current) { cancelAnimationFrame(repRAFRef.current); repRAFRef.current = null; } setRepT(parseFloat(e.target.value)); setRepPlaying(false); };

  // ── Derived ─────────────────────────────────────────────────────
  const mrna = dna.replace(/T/g, 'U');
  const codons = [];
  for (let i = 0; i + 3 <= mrna.length; i += 3) {
    const cod = mrna.slice(i, i + 3); const aa = codonAA(cod); const info = AA_TABLE[aa];
    codons.push({ codon: cod, aa: aa === '*' ? 'STOP' : info[0], full: info[1], type: info[2], isStop: aa === '*', isStart: cod === 'AUG' && i === 0 });
    if (aa === '*') break;
  }

  const REP_N = 14, REP_BASES = 'ATGCATGCATGCAT', REP_X = 160, REP_GY = 30, REP_Y0 = 70, REP_SEP = 46;
  const repPolys = [], repBalls = [];
  for (let j = 0; j < REP_N; j++) {
    const y = REP_Y0 + j * REP_GY; const frac = j / (REP_N - 1);
    const open = Math.max(0, Math.min(1, (repT - frac) * 4 + 0.5));
    const a = REP_BASES[j], t = COMP[a];
    if (open < 0.05) {
      repPolys.push({ x1:REP_X-REP_SEP/2, y1:y, x2:REP_X+REP_SEP/2, y2:y, stroke:'rgba(255,255,255,.2)' });
      repBalls.push({ cx:REP_X-REP_SEP/2, cy:y, r:7, fill:D[a], op:1 });
      repBalls.push({ cx:REP_X+REP_SEP/2, cy:y, r:7, fill:D[t], op:1 });
    } else {
      const sp = open * 70;
      const lx = REP_X - REP_SEP/2 - sp;
      repPolys.push({ x1:lx-12, y1:y, x2:lx+12, y2:y, stroke:'rgba(255,255,255,.15)' });
      repBalls.push({ cx:lx-12, cy:y, r:7, fill:D[a], op:1 });
      repBalls.push({ cx:lx+12, cy:y, r:6, fill:D[t], op:0.45+0.55*open });
      const rx = REP_X + REP_SEP/2 + sp;
      repPolys.push({ x1:rx-12, y1:y, x2:rx+12, y2:y, stroke:'rgba(255,255,255,.15)' });
      repBalls.push({ cx:rx+12, cy:y, r:7, fill:D[t], op:1 });
      repBalls.push({ cx:rx-12, cy:y, r:6, fill:D[a], op:0.45+0.55*open });
    }
  }

  const selA = HELIX_SEQ[selPair], selT = COMP[selA], triple = selA === 'G' || selA === 'C';

  const g1 = p1.split(''), g2 = p2.split('');
  let brownCount = 0;
  const punnett = [];
  for (let r = 0; r < 2; r++) for (let c = 0; c < 2; c++) {
    let geno = [g2[r], g1[c]].sort((x, y) => (x === 'B' ? -1 : 1) - (y === 'B' ? -1 : 1)).join('');
    if (geno === 'bB') geno = 'Bb';
    const isBrown = geno.includes('B'); if (isBrown) brownCount++;
    punnett.push({ geno, isBrown });
  }

  const tabSt = (on) => ({ background: on ? D.accent : 'transparent', color: on ? '#04111f' : D.muted, border: 'none', padding: '10px 16px', borderRadius: 11, fontWeight: on ? 700 : 500, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: FD });
  const btnReady = { padding: '12px 18px', borderRadius: 11, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14, background: D.accent, color: '#04111f', fontFamily: FD };
  const btnDim = { ...btnReady, background: D.panel2, color: D.muted, border: `1px solid ${D.line}` };

  return (
    <div style={{ background: `radial-gradient(1200px 700px at 80% -10%,rgba(76,201,255,.12),transparent 60%),radial-gradient(1000px 600px at 0% 100%,rgba(255,63,180,.1),transparent 55%),${D.bg}`, color: D.ink, fontFamily: FB, padding: '28px 20px 40px' }}>
      <div style={{ maxWidth: 1140, margin: '0 auto' }}>

        {/* Header */}
        <header style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 22 }}>
          <div style={{ minWidth: 280 }}>
            <div style={{ color: D.accent, fontFamily: FM, fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase' }}>● Interactive Biology</div>
            <h1 style={{ fontFamily: FD, fontWeight: 700, fontSize: 'clamp(30px,5.2vw,50px)', lineHeight: 1.02, margin: '8px 0 6px', letterSpacing: '-0.02em' }}>DNA — The Code of <span style={{ color: D.accent }}>You</span></h1>
            <p style={{ margin: 0, maxWidth: '54ch', color: D.muted, fontSize: 15.5, lineHeight: 1.55 }}>Two metres of molecule, coiled inside almost every cell, spelling out a three-billion-letter instruction manual. Zoom in, spin it, read it, copy it.</p>
          </div>
          <div style={{ display: 'flex', gap: 14, fontFamily: FM, fontSize: 12, color: D.muted, textAlign: 'right' }}>
            {[['3.1B','base pairs'],['~20k','genes'],['46','chromosomes']].map(([n,l]) => (
              <div key={l}><div style={{ fontFamily: FD, fontSize: 22, color: D.ink, fontWeight: 700 }}>{n}</div>{l}</div>
            ))}
          </div>
        </header>

        {/* Tab nav */}
        <nav style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24, position: 'sticky', top: 10, zIndex: 20, background: 'rgba(6,9,18,.7)', backdropFilter: 'blur(8px)', padding: 8, border: `1px solid ${D.line}`, borderRadius: 16 }}>
          {TABS.map(([num, label], i) => (
            <button key={i} onClick={() => setTab(i)} style={tabSt(tab === i)}>
              <span style={{ fontFamily: FM, opacity: 0.6, marginRight: 8 }}>{num}</span>{label}
            </button>
          ))}
        </nav>

        {/* ── Tab 0: The Zoom ── */}
        {tab === 0 && (
          <section style={{ display: 'grid', gridTemplateColumns: '1.1fr .9fr', gap: 26, alignItems: 'center' }}>
            <div style={{ background: D.panel, border: `1px solid ${D.line}`, borderRadius: 24, padding: 30, display: 'grid', placeItems: 'center', position: 'relative', overflow: 'hidden', minHeight: 420 }}>
              <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 50% 45%,rgba(76,201,255,.14),transparent 60%)' }} />
              <div style={{ position: 'relative', width: 300, height: 300, display: 'grid', placeItems: 'center' }}>
                {ZOOM_LEVELS.map((_, zi) => {
                  const cur = zi === zoom; const dist = zi - zoom;
                  return (
                    <div key={zi} style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', transition: 'opacity .55s,transform .55s', opacity: cur ? 1 : 0, transform: `scale(${cur ? 1 : dist < 0 ? 2.2 : 0.45})`, pointerEvents: 'none' }}>
                      <div style={{ position: 'absolute', width: 250, height: 250, borderRadius: '50%', border: '1px solid rgba(76,201,255,.25)', boxShadow: '0 0 60px rgba(76,201,255,.18) inset' }} />
                      <div style={{ width: 200, height: 200, filter: 'drop-shadow(0 0 10px rgba(76,201,255,.5))', animation: 'dnaFloaty 6s ease-in-out infinite' }} dangerouslySetInnerHTML={{ __html: ZOOM_SVG[zi] }} />
                    </div>
                  );
                })}
                <div style={{ position: 'absolute', bottom: -6, fontFamily: FM, fontSize: 12, color: D.accent, letterSpacing: '0.1em' }}>{ZOOM_LEVELS[zoom].scale}</div>
              </div>
            </div>
            <div>
              <div style={{ fontFamily: FM, fontSize: 12, letterSpacing: '0.16em', textTransform: 'uppercase', color: D.accent2 }}>The Zoom · Level {zoom + 1} of 5</div>
              <h2 style={{ fontFamily: FD, fontWeight: 700, fontSize: 30, margin: '8px 0 4px' }}>{ZOOM_LEVELS[zoom].label}</h2>
              <div style={{ color: D.accent, fontWeight: 600, marginBottom: 10 }}>{ZOOM_LEVELS[zoom].sub}</div>
              <p style={{ color: D.muted, fontSize: 16, lineHeight: 1.6, minHeight: '4.8em', margin: '0 0 22px' }}>{ZOOM_LEVELS[zoom].desc}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 20 }}>
                {ZOOM_LEVELS.map((lv, zi) => (
                  <button key={zi} onClick={() => setZoom(zi)} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', background: zi === zoom ? D.panel2 : 'transparent', border: `1px solid ${zi === zoom ? D.accent : D.line}`, color: zi === zoom ? D.ink : D.muted, padding: '10px 12px', borderRadius: 11, cursor: 'pointer', fontFamily: FB, fontWeight: 600, fontSize: 14 }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: zi === zoom ? D.accent : D.line, boxShadow: zi === zoom ? `0 0 8px ${D.accent}` : 'none', flexShrink: 0, display: 'inline-block' }} />
                    {lv.label}
                    <span style={{ marginLeft: 'auto', fontFamily: FM, fontSize: 11, opacity: 0.7 }}>{lv.scale}</span>
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setZoom(z => Math.max(0, z - 1))} style={{ flex: 1, background: D.panel2, border: `1px solid ${D.line}`, color: D.ink, fontWeight: 600, padding: 12, borderRadius: 12, cursor: 'pointer', fontFamily: FB }}>◀ Zoom out</button>
                <button onClick={() => setZoom(z => Math.min(4, z + 1))} style={{ flex: 1, background: D.accent, border: 'none', color: '#04111f', fontWeight: 700, padding: 12, borderRadius: 12, cursor: 'pointer', fontFamily: FB }}>Zoom in ▶</button>
              </div>
            </div>
          </section>
        )}

        {/* ── Tab 1: Double Helix ── */}
        {tab === 1 && (
          <section style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 26, alignItems: 'stretch' }}>
            <div style={{ background: `linear-gradient(180deg,${D.bg2},${D.panel})`, border: `1px solid ${D.line}`, borderRadius: 24, padding: 20, display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontFamily: FM, fontSize: 12, color: D.muted, letterSpacing: '0.12em' }}>DRAG TO SPIN · CLICK A RUNG</div>
              <div style={{ flex: 1, display: 'grid', placeItems: 'center', perspective: 1100, touchAction: 'none', cursor: 'grab', minHeight: 440 }}
                onPointerDown={onHelixDown} onPointerMove={onHelixMove} onPointerUp={onHelixUp} onPointerLeave={onHelixUp}>
                <div style={{ transform: 'rotateX(-10deg)', transformStyle: 'preserve-3d' }}>
                  <div ref={attachHelix} style={{ position: 'relative', width: 1, height: 1, transformStyle: 'preserve-3d' }}>
                    {HELIX_SEQ.split('').map((a, i) => {
                      const tb = COMP[a]; const y = (i - (HN - 1) / 2) * H_GAP; const sel = i === selPair;
                      return (
                        <div key={i} onClick={() => setSelPair(i)} style={{ position: 'absolute', top: '50%', left: '50%', width: 0, height: 0, transform: `translateY(${y}px) rotateY(${i * H_TWIST}deg)`, transformStyle: 'preserve-3d', cursor: 'pointer' }}>
                          <div style={{ position: 'absolute', left: -H_R, top: -1.5, width: H_R * 2, height: 3, background: sel ? 'rgba(255,255,255,.6)' : 'rgba(255,255,255,.18)' }} />
                          <div style={{ position: 'absolute', left: -H_R - 8, top: -8, width: 16, height: 16, borderRadius: '50%', background: D[a], boxShadow: `0 0 10px ${D[a]},0 0 3px ${D[a]}` }} />
                          <div style={{ position: 'absolute', left: H_R - 8, top: -8, width: 16, height: 16, borderRadius: '50%', background: D[tb], boxShadow: `0 0 10px ${D[tb]},0 0 3px ${D[tb]}` }} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, borderTop: `1px solid ${D.line}`, paddingTop: 14 }}>
                <button onClick={() => setSpinning(s => !s)} style={{ background: D.panel2, border: `1px solid ${D.line}`, color: D.ink, fontWeight: 600, padding: '9px 16px', borderRadius: 10, cursor: 'pointer', fontFamily: FD }}>{spinning ? '⏸ Pause' : '▶ Spin'}</button>
                <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, color: D.muted, fontSize: 13, fontFamily: FB }}>
                  Speed<input type="range" min="0" max="3" step="0.1" value={speed} onChange={e => setSpeed(parseFloat(e.target.value))} style={{ flex: 1, accentColor: D.accent }} />
                </label>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ background: D.panel, border: `1px solid ${D.line}`, borderRadius: 20, padding: 22 }}>
                <h2 style={{ fontFamily: FD, fontWeight: 700, fontSize: 24, margin: '0 0 6px' }}>The twisted ladder</h2>
                <p style={{ color: D.muted, fontSize: 15, lineHeight: 1.55, margin: '0 0 14px' }}>Two strands run in <strong style={{ color: D.ink }}>opposite directions</strong>, held together by a sugar-phosphate backbone. The rungs are pairs of bases — and the pairing is never random.</p>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {[['A','T','=','2 hydrogen bonds'],['G','C','≡','3 hydrogen bonds']].map(([a,b,sym,note]) => (
                    <div key={a} style={{ flex: 1, minWidth: 120, background: D.panel2, border: `1px solid ${D.line}`, borderRadius: 14, padding: 12, textAlign: 'center' }}>
                      <span style={{ color: D[a], fontWeight: 700, fontFamily: FM }}>{a}</span>
                      <span style={{ color: D.muted }}> {sym} </span>
                      <span style={{ color: D[b], fontWeight: 700, fontFamily: FM }}>{b}</span>
                      <div style={{ color: D.muted, fontSize: 12, marginTop: 4 }}>{note}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ background: D.panel, border: `1px solid ${D.line}`, borderRadius: 20, padding: 22, flex: 1 }}>
                <div style={{ fontFamily: FM, fontSize: 12, color: D.accent, letterSpacing: '0.14em' }}>SELECTED BASE PAIR</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '14px 0' }}>
                  {[selA, selT].map((b, bi) => (
                    <React.Fragment key={bi}>
                      {bi === 1 && <div style={{ textAlign: 'center', color: D.muted, fontSize: 12, fontFamily: FM }}>{triple ? '≡' : '='}<div style={{ marginTop: 2 }}>{(triple ? 3 : 2)} H-bonds</div></div>}
                      <div style={{ width: 52, height: 52, borderRadius: '50%', background: D[b], display: 'grid', placeItems: 'center', fontFamily: FM, fontWeight: 700, fontSize: 22, color: '#04111f', boxShadow: `0 0 22px ${D[b]}` }}>{b}</div>
                    </React.Fragment>
                  ))}
                </div>
                <p style={{ color: D.muted, fontSize: 14.5, lineHeight: 1.55, margin: 0 }}>{AA_NAMES[selA]} ({selA}) always pairs with {AA_NAMES[selT]} ({selT}). They lock together with {triple ? 'three' : 'two'} hydrogen bonds — that strict pairing is what lets DNA copy itself perfectly.</p>
              </div>
            </div>
          </section>
        )}

        {/* ── Tab 2: The Code ── */}
        {tab === 2 && (
          <section style={{ background: D.panel, border: `1px solid ${D.line}`, borderRadius: 24, padding: 28 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 14, marginBottom: 18 }}>
              <div>
                <h2 style={{ fontFamily: FD, fontWeight: 700, fontSize: 26, margin: '0 0 4px' }}>From gene to protein</h2>
                <p style={{ color: D.muted, fontSize: 15, margin: 0, maxWidth: '60ch' }}>Click any DNA letter to change it. Transcribe to mRNA, then translate the codons into a protein chain.</p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={doMutate} style={{ background: D.panel2, border: `1px solid ${D.accent2}`, color: D.accent2, fontWeight: 700, padding: '10px 16px', borderRadius: 10, cursor: 'pointer', fontFamily: FD }}>⚡ Mutate</button>
                <button onClick={() => { setDna('ATGTTCGGCAGCTAA'); setCodeStep(0); setMutNote(''); }} style={{ background: D.panel2, border: `1px solid ${D.line}`, color: D.ink, fontWeight: 600, padding: '10px 16px', borderRadius: 10, cursor: 'pointer', fontFamily: FD }}>↺ Reset</button>
              </div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontFamily: FM, fontSize: 12, color: D.muted, letterSpacing: '0.12em', marginBottom: 8 }}>DNA · CODING STRAND</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {dna.split('').map((b, i) => (
                  <button key={i} onClick={() => cycleBase(i)} style={{ width: 34, height: 42, display: 'grid', placeItems: 'center', fontFamily: FM, fontWeight: 700, fontSize: 18, color: '#04111f', background: BC[b], border: `1px solid ${D.line}`, borderRadius: 8, cursor: 'pointer' }}>{b}</button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0' }}><div style={{ color: D.accent, fontFamily: FM, fontSize: 13 }}>↓ transcription (T → U)</div></div>
            <div style={{ marginBottom: 18, opacity: codeStep >= 1 ? 1 : 0.12, transition: 'opacity .4s' }}>
              <div style={{ fontFamily: FM, fontSize: 12, color: D.muted, letterSpacing: '0.12em', marginBottom: 8 }}>messenger RNA</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {mrna.split('').map((b, i) => (
                  <div key={i} style={{ width: 34, height: 42, display: 'grid', placeItems: 'center', fontFamily: FM, fontWeight: 700, fontSize: 18, color: '#04111f', background: BC[b], border: `1px solid ${D.line}`, borderRadius: 8, boxShadow: `0 0 10px ${b === 'U' ? 'rgba(192,139,255,.4)' : 'rgba(76,201,255,.25)'}` }}>{b}</div>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0' }}><div style={{ color: D.accent, fontFamily: FM, fontSize: 13 }}>↓ translation (read in triplets)</div></div>
            <div style={{ opacity: codeStep >= 2 ? 1 : 0.12, transition: 'opacity .4s' }}>
              <div style={{ fontFamily: FM, fontSize: 12, color: D.muted, letterSpacing: '0.12em', marginBottom: 8 }}>PROTEIN · CHAIN OF AMINO ACIDS</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                {codons.map((cd, ci) => (
                  <React.Fragment key={ci}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                      <div style={{ fontFamily: FM, fontSize: 13, color: D.muted, letterSpacing: '0.05em' }}>{cd.codon}</div>
                      <div style={{ padding: cd.isStop ? '10px 12px' : '14px 12px', minWidth: 58, textAlign: 'center', borderRadius: 12, fontFamily: FD, fontWeight: 700, fontSize: 15, color: '#04111f', background: AA_COL[cd.type], boxShadow: `0 0 16px ${AA_COL[cd.type]}55`, border: cd.isStart ? '2px solid #fff' : 'none' }} title={cd.full}>{cd.aa}</div>
                      <div style={{ fontSize: 11, color: D.muted }}>{cd.full}</div>
                    </div>
                    {ci < codons.length - 1 && !cd.isStop && <div style={{ width: 14, height: 3, background: D.line, borderRadius: 2 }} />}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div style={{ marginTop: 22, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <button onClick={() => setCodeStep(s => Math.max(1, s))} style={codeStep < 1 ? btnReady : btnDim}>1 · Transcribe →</button>
              <button onClick={() => setCodeStep(2)} style={codeStep === 1 ? btnReady : btnDim}>2 · Translate →</button>
              {mutNote && <div style={{ color: mutNote.includes('Nonsense') ? D.T : mutNote.includes('Missense') ? D.G : D.A, fontSize: 14, fontWeight: 600, fontFamily: FB }}>{mutNote}</div>}
            </div>
          </section>
        )}

        {/* ── Tab 3: Replication ── */}
        {tab === 3 && (
          <section style={{ display: 'grid', gridTemplateColumns: '.9fr 1.1fr', gap: 26, alignItems: 'center' }}>
            <div>
              <div style={{ fontFamily: FM, fontSize: 12, letterSpacing: '0.16em', textTransform: 'uppercase', color: D.accent2 }}>Replication</div>
              <h2 style={{ fontFamily: FD, fontWeight: 700, fontSize: 28, margin: '8px 0 10px' }}>Every copy keeps one original strand</h2>
              <p style={{ color: D.muted, fontSize: 16, lineHeight: 1.6, margin: '0 0 16px' }}>Before a cell divides, <strong style={{ color: D.ink }}>helicase</strong> unzips the double helix down the middle. Each old strand becomes a template — free-floating bases snap in following A–T and G–C rules, building two identical molecules.</p>
              <p style={{ color: D.muted, fontSize: 15, lineHeight: 1.6, margin: '0 0 22px' }}>This is called <strong style={{ color: D.accent }}>semiconservative</strong> replication — every new helix is half old, half new.</p>
              <button onClick={repPlay} style={{ background: D.accent, border: 'none', color: '#04111f', fontWeight: 700, padding: '13px 22px', borderRadius: 12, cursor: 'pointer', width: '100%', fontFamily: FD }}>{repPlaying ? '⏸ Pause' : repT >= 0.999 ? '↺ Replay' : '▶ Unzip & copy'}</button>
              <label style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, color: D.muted, fontSize: 13, fontFamily: FB }}>
                Scrub<input type="range" min="0" max="1" step="0.01" value={repT} onChange={repScrub} style={{ flex: 1, accentColor: D.accent }} />
              </label>
            </div>
            <div style={{ background: `linear-gradient(180deg,${D.bg2},${D.panel})`, border: `1px solid ${D.line}`, borderRadius: 24, padding: 18, display: 'grid', placeItems: 'center', minHeight: 460 }}>
              <svg viewBox="0 0 320 460" style={{ width: '100%', maxWidth: 380, overflow: 'visible' }}>
                {repPolys.map((p, pi) => <line key={pi} x1={p.x1} y1={p.y1} x2={p.x2} y2={p.y2} style={{ stroke: p.stroke, strokeWidth: 2 }} />)}
                {repBalls.map((b, bi) => <circle key={bi} cx={b.cx} cy={b.cy} r={b.r} style={{ fill: b.fill, opacity: b.op, filter: `drop-shadow(0 0 5px ${b.fill})` }} />)}
              </svg>
            </div>
          </section>
        )}

        {/* ── Tab 4: Genome & You ── */}
        {tab === 4 && (
          <section style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.3fr .7fr', gap: 24 }}>
              <div style={{ background: D.panel, border: `1px solid ${D.line}`, borderRadius: 24, padding: 24 }}>
                <h2 style={{ fontFamily: FD, fontWeight: 700, fontSize: 24, margin: '0 0 4px' }}>Your 23 pairs</h2>
                <p style={{ color: D.muted, fontSize: 14.5, margin: '0 0 18px' }}>All your DNA is packed into 46 chromosomes — 23 from each parent. Tap any pair.</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12,1fr)', gap: 7, alignItems: 'end' }}>
                  {CHR_LEN.map((len, idx) => {
                    const num = idx + 1; const h = 24 + len / 100 * 40; const sel = selChr === num;
                    return (
                      <button key={num} onClick={() => setSelChr(num)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'transparent', border: 'none', cursor: 'pointer', padding: 2 }}>
                        <div style={{ display: 'flex', gap: 2, alignItems: 'end', height: 64 }}>
                          {[0,1].map(s => <div key={s} style={{ width: 7, height: h, borderRadius: 4, background: sel ? D.accent : 'linear-gradient(180deg,#5b7bb5,#2f4a78)', boxShadow: sel ? `0 0 10px ${D.accent}` : 'none' }} />)}
                        </div>
                        <div style={{ fontSize: 10, color: sel ? D.accent : D.muted, fontFamily: FM }}>{num}</div>
                      </button>
                    );
                  })}
                  {/* Sex pair */}
                  {(() => { const sel = selChr === 23;
                    return (
                      <button onClick={() => setSelChr(23)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'transparent', border: 'none', cursor: 'pointer', padding: 2 }}>
                        <div style={{ display: 'flex', gap: 2, alignItems: 'end', height: 64 }}>
                          <div style={{ width: 7, height: 48, borderRadius: 4, background: sel ? D.accent2 : 'linear-gradient(180deg,#b56fa0,#6a3a5c)', boxShadow: sel ? `0 0 10px ${D.accent2}` : 'none' }} />
                          <div style={{ width: 7, height: sex === 'XX' ? 48 : 28, borderRadius: 4, background: sel ? D.accent2 : 'linear-gradient(180deg,#b56fa0,#6a3a5c)', boxShadow: sel ? `0 0 10px ${D.accent2}` : 'none' }} />
                        </div>
                        <div style={{ fontSize: 10, color: sel ? D.accent2 : D.muted, fontFamily: FM }}>{sex}</div>
                      </button>
                    );
                  })()}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
                  <span style={{ color: D.muted, fontSize: 13, fontFamily: FB }}>Biological sex pair:</span>
                  {['XX','XY'].map(s => <button key={s} onClick={() => setSex(s)} style={{ background: sex === s ? D.accent : D.panel2, color: sex === s ? '#04111f' : D.muted, border: `1px solid ${sex === s ? D.accent : D.line}`, padding: '7px 14px', borderRadius: 9, fontWeight: 700, cursor: 'pointer', fontFamily: FD }}>{s}</button>)}
                </div>
              </div>
              <div style={{ background: `linear-gradient(180deg,${D.panel2},${D.panel})`, border: `1px solid ${D.line}`, borderRadius: 24, padding: 24, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ fontFamily: FM, fontSize: 12, color: D.accent, letterSpacing: '0.14em' }}>{selChr === 23 ? 'SEX CHROMOSOMES' : 'CHROMOSOME'}</div>
                <div style={{ fontFamily: FD, fontWeight: 700, fontSize: 30, margin: '6px 0 10px' }}>{selChr === 23 ? sex : 'Number ' + selChr}</div>
                <p style={{ color: D.muted, fontSize: 15, lineHeight: 1.6, margin: 0 }}>
                  {selChr === 23
                    ? (sex === 'XX' ? 'Two X chromosomes — typically female. You inherited one X from each parent.' : 'One X and one smaller Y — typically male. The Y, passed father to son, carries the SRY gene that triggers male development.')
                    : (CHR_FACTS[selChr] || 'One of your 22 autosomes. You carry two copies — one from each parent — each a long, coiled thread of DNA holding hundreds of genes.')}
                </p>
              </div>
            </div>
            {/* Punnett / Heredity */}
            <div style={{ background: D.panel, border: `1px solid ${D.line}`, borderRadius: 24, padding: 26 }}>
              <h2 style={{ fontFamily: FD, fontWeight: 700, fontSize: 24, margin: '0 0 4px' }}>Why you look like your parents</h2>
              <p style={{ color: D.muted, fontSize: 14.5, margin: '0 0 20px', maxWidth: '70ch' }}>You inherit one copy of each gene from each parent. Dominant alleles (<strong style={{ color: D.ink }}>B</strong> = brown) mask recessive ones (<strong style={{ color: D.ink }}>b</strong> = blue). Set each parent and see the odds.</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 30, alignItems: 'center' }}>
                <div>
                  {[['Parent 1', p1, setP1], ['Parent 2', p2, setP2]].map(([label, val, setter], pi) => (
                    <div key={pi} style={{ marginBottom: pi === 0 ? 18 : 0 }}>
                      <div style={{ color: D.muted, fontSize: 13, marginBottom: 6, fontFamily: FB }}>{label}</div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {['BB','Bb','bb'].map(g => <button key={g} onClick={() => setter(g)} style={{ background: val === g ? '#a06a3a' : D.panel2, color: val === g ? '#04111f' : D.muted, border: `1px solid ${val === g ? '#a06a3a' : D.line}`, padding: '7px 14px', borderRadius: 9, fontWeight: 700, cursor: 'pointer', fontFamily: FM }}>{g}</button>)}
                      </div>
                    </div>
                  ))}
                  <div style={{ marginTop: 22, display: 'flex', gap: 18 }}>
                    <div><div style={{ fontFamily: FD, fontSize: 28, fontWeight: 700, color: '#a06a3a' }}>{brownCount / 4 * 100}%</div><div style={{ color: D.muted, fontSize: 12 }}>brown eyes</div></div>
                    <div><div style={{ fontFamily: FD, fontSize: 28, fontWeight: 700, color: '#4a90d9' }}>{(4 - brownCount) / 4 * 100}%</div><div style={{ color: D.muted, fontSize: 12 }}>blue eyes</div></div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', gridTemplateRows: 'auto 1fr 1fr', gap: 8, maxWidth: 360, justifySelf: 'center' }}>
                  <div />
                  {[g1[0], g1[1]].map((h, hi) => <div key={hi} style={{ display: 'grid', placeItems: 'center', fontFamily: FM, fontWeight: 700, fontSize: 18, color: D.ink, padding: 6 }}>{h}</div>)}
                  {g2.map((h, ri) => (
                    <React.Fragment key={ri}>
                      <div style={{ display: 'grid', placeItems: 'center', fontFamily: FM, fontWeight: 700, fontSize: 18, color: D.ink, padding: 6 }}>{h}</div>
                      {punnett.slice(ri * 2, ri * 2 + 2).map((cell, ci) => (
                        <div key={ci} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, background: D.panel2, border: `1px solid ${D.line}`, borderRadius: 12, padding: '14px 6px' }}>
                          <span style={{ fontFamily: FM, fontWeight: 700, fontSize: 17, color: cell.isBrown ? '#c98a4a' : '#6aa6e6' }}>{cell.geno}</span>
                          <span style={{ width: 22, height: 22, borderRadius: '50%', background: cell.isBrown ? 'radial-gradient(circle at 35% 35%,#a06a3a,#5c3a1c)' : 'radial-gradient(circle at 35% 35%,#7db4ec,#2f6bb0)', boxShadow: `0 0 8px ${cell.isBrown ? '#a06a3a88' : '#4a90d988'}`, display: 'block' }} />
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

      </div>
    </div>
  );
}
